import { Create } from '@refinedev/mui'
import { Box, TextField, Autocomplete } from '@mui/material'
import { useForm } from '@refinedev/react-hook-form'
import { Controller } from 'react-hook-form'
import { NZ_REGIONS, getCouncilsForRegion } from '@/constants/nzRegionsCouncils'
import { adminFacingMessage } from '@/utils/adminFacingError'

export const RegionCreate = () => {
  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    refineCoreProps: {
      action: 'create',
      resource: 'regions',
      errorNotification: (error) => ({
        message: 'Could not add location',
        description:
          adminFacingMessage(error) ??
          'Something went wrong when adding the location. Please try again.',
        type: 'error',
      }),
    },
  })

  const country = watch('country')
  const region = watch('region')
  const isNewZealand = country === 'New Zealand'

  // Get councils filtered by selected region
  const councilOptions = region ? getCouncilsForRegion(region) : []

  return (
    <Create isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column' }}
        autoComplete="off"
      >
        <TextField
          {...register('name', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />
        <TextField
          {...register('country', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.country}
          helperText={(errors as any)?.country?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Country"
          name="country"
        />
        {isNewZealand ? (
          <Controller
            name="region"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                value={field.value || null}
                onChange={(_, value) => {
                  field.onChange(value)
                  // Clear council when region changes
                  setValue('council', '')
                }}
                options={[...NZ_REGIONS]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region"
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!(errors as any)?.region}
                    helperText={(errors as any)?.region?.message}
                  />
                )}
              />
            )}
          />
        ) : (
          <TextField
            {...register('region', { required: false })}
            error={!!(errors as any)?.region}
            helperText={(errors as any)?.region?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Region"
            name="region"
          />
        )}
        {isNewZealand ? (
          <Controller
            name="council"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                options={councilOptions}
                disabled={!region}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Council"
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!(errors as any)?.council}
                    helperText={
                      (errors as any)?.council?.message ||
                      (!region ? 'Select a region first' : '')
                    }
                  />
                )}
              />
            )}
          />
        ) : (
          <TextField
            {...register('council', { required: false })}
            error={!!(errors as any)?.council}
            helperText={(errors as any)?.council?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Council"
            name="council"
          />
        )}
        <TextField
          {...register('major', { required: false })}
          error={!!(errors as any)?.major}
          helperText={(errors as any)?.major?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Major (City, Town)"
          name="major"
        />
        <TextField
          {...register('suburb', { required: false })}
          error={!!(errors as any)?.suburb}
          helperText={(errors as any)?.suburb?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Suburb"
          name="suburb"
        />
        <TextField
          {...register('additional_names', { required: false })}
          error={!!(errors as any)?.additional_names}
          helperText={(errors as any)?.suburb?.additional_names}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Additional names (comma separated)"
          name="additional_names"
        />
      </Box>
    </Create>
  )
}
